/**
 * @file Components/Ui/Skeleton.jsx
 * @description Skeleton loading placeholder component. Provides animated
 * placeholder shapes (text, image, card) for content loading states.
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * Base Skeleton component for loading states
 */
export const Skeleton = ({ className = '', variant = 'rectangular', animation = 'pulse' }) => {
  const baseClasses = 'bg-gray-300';
  const animationClasses = animation === 'pulse' ? 'animate-pulse' : animation === 'wave' ? 'animate-shimmer' : '';
  const variantClasses = variant === 'circular' ? 'rounded-full' : variant === 'rounded' ? 'rounded-lg' : 'rounded';
  
  return (
    <div className={`${baseClasses} ${animationClasses} ${variantClasses} ${className}`} />
  );
};

Skeleton.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(['rectangular', 'circular', 'rounded']),
  animation: PropTypes.oneOf(['pulse', 'wave', 'none'])
};

/**
 * Tour Card Skeleton
 */
export const TourCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <Skeleton className="h-48 w-full" variant="rectangular" />
    <div className="p-4 space-y-3">
      <Skeleton className="h-6 w-3/4" variant="rounded" />
      <Skeleton className="h-4 w-full" variant="rounded" />
      <Skeleton className="h-4 w-5/6" variant="rounded" />
      <div className="flex justify-between items-center mt-4">
        <Skeleton className="h-4 w-20" variant="rounded" />
        <Skeleton className="h-8 w-24" variant="rounded" />
      </div>
    </div>
  </div>
);

/**
 * Blog Card Skeleton
 */
export const BlogCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <Skeleton className="h-56 w-full" variant="rectangular" />
    <div className="p-6 space-y-3">
      <Skeleton className="h-7 w-full" variant="rounded" />
      <Skeleton className="h-4 w-full" variant="rounded" />
      <Skeleton className="h-4 w-4/5" variant="rounded" />
      <div className="flex items-center gap-4 mt-4">
        <Skeleton className="h-10 w-10" variant="circular" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-24" variant="rounded" />
          <Skeleton className="h-3 w-32" variant="rounded" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * List Item Skeleton
 */
export const ListItemSkeleton = () => (
  <div className="flex items-center gap-4 p-4 bg-white rounded-lg">
    <Skeleton className="h-16 w-16" variant="circular" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-3/4" variant="rounded" />
      <Skeleton className="h-3 w-1/2" variant="rounded" />
    </div>
  </div>
);

/**
 * Table Row Skeleton
 */
export const TableRowSkeleton = ({ columns = 4 }) => (
  <tr className="border-b">
    {[...Array(columns)].map((_, i) => (
      <td key={i} className="px-6 py-4">
        <Skeleton className="h-4 w-full" variant="rounded" />
      </td>
    ))}
  </tr>
);

TableRowSkeleton.propTypes = {
  columns: PropTypes.number
};

/**
 * Profile Skeleton
 */
export const ProfileSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <div className="flex items-center gap-4 mb-6">
      <Skeleton className="h-20 w-20" variant="circular" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-6 w-48" variant="rounded" />
        <Skeleton className="h-4 w-32" variant="rounded" />
      </div>
    </div>
    <div className="space-y-4">
      <Skeleton className="h-10 w-full" variant="rounded" />
      <Skeleton className="h-10 w-full" variant="rounded" />
      <Skeleton className="h-10 w-full" variant="rounded" />
      <Skeleton className="h-10 w-2/3" variant="rounded" />
    </div>
  </div>
);

/**
 * Grid Skeleton - renders multiple skeleton items in a grid
 */
export const GridSkeleton = ({ count = 6, component: Component = TourCardSkeleton, columns = 3 }) => {
  const gridClasses = `grid gap-6 ${
    columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
    columns === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
    columns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
    'grid-cols-1'
  }`;

  return (
    <div className={gridClasses}>
      {[...Array(count)].map((_, i) => (
        <Component key={i} />
      ))}
    </div>
  );
};

GridSkeleton.propTypes = {
  count: PropTypes.number,
  component: PropTypes.elementType,
  columns: PropTypes.number
};

export default Skeleton;
